import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { setupAxiosInterceptors } from './axiosSetup';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/de';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './css/CalendarView.css';
import cfg from '../config';




moment.locale('de');
const localizer = momentLocalizer(moment);

const typeColors = [
  '#0d6efd', '#dc3545', '#198754', '#fd7e14',
  '#6f42c1', '#20c997', '#e83e8c', '#795548',
];

const loadColorMap = () => {
  const stored = localStorage.getItem('eventTypeColors');
  return stored ? JSON.parse(stored) : {};
};

const saveColorMap = (map) => {
  localStorage.setItem('eventTypeColors', JSON.stringify(map));
};

const CustomToolbar = ({ label, onNavigate }) => {
  const handlePrev = () => onNavigate('PREV');
  const handleNext = () => onNavigate('NEXT');
  const handleToday = () => onNavigate('TODAY');

  const monthYear = moment(label, 'MMMM YYYY').format('MMMM YYYY');

  return (
    <div
      className="d-flex justify-content-between align-items-center mb-2 p-2"
      style={{ backgroundColor: '#f8f9fa', borderRadius: '5px' }}
    >
      <div>
        <button className="btn btn-sm btn-outline-secondary me-2" onClick={handleToday}>Heute</button>
        <button className="btn btn-sm btn-outline-secondary me-2" onClick={handlePrev}>‹</button>
        <button className="btn btn-sm btn-outline-secondary" onClick={handleNext}>›</button>
      </div>
      <h5 className="m-0 text-capitalize text-dark">{monthYear}</h5>
    </div>
  );
};

const CalendarView = () => {
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const colorMapRef = useRef(loadColorMap());

  const getTypeColor = (typeId) => {
    if (!typeId) return '#0d6efd';
    if (!colorMapRef.current[typeId]) {
      const index = Object.keys(colorMapRef.current).length % typeColors.length;
      colorMapRef.current[typeId] = typeColors[index];
      saveColorMap(colorMapRef.current);
    }
    return colorMapRef.current[typeId];
  };

  useEffect(() => {
    setupAxiosInterceptors(navigate);

    const loadData = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const typesResp = await api.get("/api/event-types", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const types = typesResp.data || [];
        setEventTypes(types);

        const eventsResp = await api.get("/api/events", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const formattedEvents = (eventsResp.data || []).map(e => {
          const startDate = e.date; // z.B. "2025-11-10"
          const time = e.time || '00:00'; // z.B. "14:30"
        
          // sicherer Moment-Parse in lokaler Zeit
          const start = moment(`${startDate} ${time}`, 'YYYY-MM-DD HH:mm').toDate();
        
          return {
            ...e,
            start,
            end: start, // wenn Dauer = 0
          };
        });
        

        formattedEvents.forEach(e => getTypeColor(e.typeId));
        setEvents(formattedEvents);
        setError(null);
      } catch (err) {
        console.error("Fehler beim Laden der Daten:", err);
        setError(err.message || "Unbekannter Fehler beim Laden der Termine.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [navigate]);

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: getTypeColor(event.typeId),
      color: 'white',
      borderRadius: '5px',
      border: 'none',
      padding: '2px 5px',
    },
  });

  const handleSelectEvent = (event) => setSelectedEvent(event);
  const handleCloseModal = () => setSelectedEvent(null);

  const formatMoveField = (field) => {
    if (!field || field.value === null || field.value === undefined) return null;
    switch (field.dataType) {
      case 'boolean':   return field.value ? 'Ja' : 'Nein';
      case 'date':      return moment(field.value).isValid() ? moment(field.value).format('DD.MM.YYYY') : String(field.value);
      case 'time':      return moment(String(field.value), 'HH:mm:ss').isValid() ? moment(String(field.value), 'HH:mm:ss').format('HH:mm') + ' Uhr' : String(field.value);
      case 'datetime':  return moment(field.value).isValid() ? moment(field.value).format('DD.MM.YYYY HH:mm') + ' Uhr' : String(field.value);
      case 'departures': return null;
      default:          return field.value != null ? String(field.value) : null;
    }
  };

  const allTypesMap = {};
  eventTypes.forEach(t => allTypesMap[t.id] = t.name);
  events.forEach(e => {
    if (e.typeId && !allTypesMap[e.typeId]) allTypesMap[e.typeId] = e.typeName;
  });
  const allTypes = Object.entries(allTypesMap);

  return (
    <div className="container mt-4">
      <h3>Kalender</h3>
      {error && <p className="text-danger">{error}</p>}
      {loading ? (
        <p>Lade Termine...</p>
      ) : (
        <>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            date={selectedDate}
            onNavigate={(date, view) => setSelectedDate(date)} // immer Datum setzen
            onDrillDown={(date, view) => {
              setSelectedDate(date); // Datum auf angeklickten Tag setzen
              // den View-Change nicht blockieren
            }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleSelectEvent}
            popup
            views={['month', 'week', 'day']}
            defaultView="month"
            components={{ toolbar: CustomToolbar }}
            messages={{
              showMore: total => `+${total} weitere`,
              month: 'Monat',
              week: 'Woche',
              day: 'Tag',
              today: 'Heute',
              previous: 'Zurück',
              next: 'Weiter',
            }}
          />


          {allTypes.length > 0 && (
            <div className="mt-3">
              <h6>Legende:</h6>
              <div className="d-flex flex-wrap gap-2">
                {allTypes.map(([id, name]) => (
                  <span
                    key={id}
                    className="badge"
                    style={{
                      backgroundColor: getTypeColor(id),
                      color: 'white',
                      padding: '5px 10px',
                      borderRadius: '5px',
                      fontSize: '0.85rem',
                    }}
                  >
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* === MODAL NARRENZUNFT STYLE === */}
          {selectedEvent && (
            <div className="modal-overlay" onClick={handleCloseModal}>
              <div
                className="modal-box animate-in"
                onClick={(e) => e.stopPropagation()}
              >
                <button className="modal-close" onClick={handleCloseModal}>
                  &times;
                </button>

                <h2 className="modal-title">{selectedEvent.title}</h2>

                <div className="modal-section">
                  <p><strong>Datum:</strong> {moment(selectedEvent.start).format('DD.MM.YYYY')}</p>

                  {selectedEvent.typeId && (
                    <p>
                      <strong>Typ:</strong>{' '}
                      <span
                        style={{
                          color: '#fff',
                          backgroundColor: getTypeColor(selectedEvent.typeId),
                          padding: '2px 6px',
                          borderRadius: '4px',
                        }}
                      >
                        {selectedEvent.typeName}
                      </span>
                    </p>
                  )}

                  {selectedEvent.start &&
                    moment(selectedEvent.start).format('HH:mm') !== '00:00' && (
                      <p>
                        <strong>Uhrzeit:</strong>{' '}
                        {moment(selectedEvent.start).format('HH:mm')} Uhr
                      </p>
                    )}

                  {selectedEvent.description && (
                    <p>
                      <strong>Beschreibung:</strong>{' '}
                      {selectedEvent.description}
                    </p>
                  )}

                  {selectedEvent.memberName && (
                    <p>
                      <strong>Person:</strong>{' '}
                      {selectedEvent.memberName}
                    </p>
                  )}

                  {selectedEvent.forZunftrat === 1 && (
                    <div className="alert alert-info mt-3 p-2">
                      <i className="bi bi-lock-fill me-2"></i>
                      Dieser Termin ist nur für {cfg.adminRoleName} sichtbar.
                    </div>
                  )}


                </div>

                {selectedEvent.isMove && selectedEvent.move?.fields?.length > 0 && (
                  <>
                    <hr className="modal-divider" />
                    <h3 className="modal-subtitle">Details zur Veranstaltung</h3>
                    <ul className="list-unstyled mb-0">
                      {selectedEvent.move.fields.map((f, i) => {
                        if (f.dataType === 'departures') {
                          if (!Array.isArray(f.value) || f.value.length === 0) return null;
                          return f.value.map((d, j) => (
                            <li key={`dep-${j}`}><strong>Abfahrt {d.label}:</strong> {moment(String(d.time), 'HH:mm:ss').format('HH:mm')} Uhr</li>
                          ));
                        }
                        const display = formatMoveField(f);
                        if (display == null) return null;
                        return <li key={i}><strong>{f.frontendName}:</strong> {display}</li>;
                      })}
                    </ul>
                  </>
                )}

                <div className="modal-actions">
                  <button className="btn-cancel" onClick={handleCloseModal}>
                    Schließen
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CalendarView;
